import { useEffect, useRef, useState } from 'react';
import useChatActions from '../../../../../../Chat.actions.ts';
import { ChatEnum } from '../../../../../../Chat.enum.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { useMessageBarContext } from '../../../../MessageBar.hook.ts';
import { tLocationPlace } from '../../../../../../Chat.types.ts';
import {
  tGoogleMapsActionHook,
  tPickedPlace,
} from './GoogleMapsAction.types.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';

const useGoogleMapsAction = (): tGoogleMapsActionHook => {
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  // INT-1720: the place picked in the search, sent with the pin.
  const [place, setPlace] = useState<tLocationPlace | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { sendLocationMessage } = useChatActions();
  const { setSelectedAction } = useMessageBarContext();
  const { setBlockTextArea, selectedChatLeadId } = useChatContext();

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          LLog.error(
            {
              component: LOG_COMPONENTS.CHAT,
              method: 'GoogleMapsAction.hooks',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Error getting user location',
            error as unknown as Error,
          );
        },
      );
    }
  }, [navigator.geolocation]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      setUserLocation({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
      // The pin left the place: what goes out is a bare pin again.
      setPlace(null);
    }
  };

  const handlePlacePicked = (picked: tPickedPlace) => {
    setUserLocation(picked.location);
    setPlace(picked.place);
  };

  const clearPlace = () => setPlace(null);

  const handleClickDropDown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    (document.activeElement as HTMLElement)?.blur();
  };

  const handleSelectLocation = async () => {
    const capturedLeadId = selectedChatLeadId;
    setBlockTextArea(true);
    try {
      if (userLocation) {
        await sendLocationMessage(userLocation.lat, userLocation.lng, place);
      }
      setIsDropdownOpen(false);
      setSelectedAction(ChatEnum.CHAT);
    } finally {
      if (selectedChatLeadId === capturedLeadId) {
        setBlockTextArea(false);
      }
    }
  };

  const handleMarkerClick = (event: google.maps.MapMouseEvent) => {
    event.domEvent.stopPropagation();
    setIsDropdownOpen(true);
  };

  return {
    userLocation,
    setUserLocation,
    place,
    handlePlacePicked,
    clearPlace,
    isDropdownOpen,
    setIsDropdownOpen,
    handleMarkerDragEnd,
    handleClickDropDown,
    handleSelectLocation,
    handleMarkerClick,
    dropdownRef,
  };
};

export default useGoogleMapsAction;
